// Pure geometry for the timeline. No canvas, no React, no fetch -- everything here is a
// function of numbers, which is what makes the zoom and pan behaviour testable without a
// browser. The renderer and the pointer handlers both go through these.
//
// This module is the view itself: the window onto the timeline, and the arithmetic that
// moves it. Everything here is a function of a `View` and a number.

/** Milliseconds per CSS pixel at each end of the zoom range. */
export const MIN_MS_PER_PX = 20; // ~1 min across a 1600 px viewport
export const MAX_MS_PER_PX = 600_000; // ~11 days across a 1600 px viewport

/** Below this width a block gets no label; it is drawn but not written on. */
export const LABEL_MIN_PX = 40;

/**
 * The widest window that still draws the jukebox lane as separate tracks.
 *
 * Past this the lane stops being a row of blocks. Every track in it loses its name and the
 * edges it shares with its neighbours, and a stretch of back-to-back tracks reads as one
 * section of "there was music here" -- which is all it can honestly say at that scale.
 *
 * All at once, rather than per block on width. Collapsing a block at a time leaves a
 * patchwork: the one eight-minute track in an hour of three-minute ones keeps its name and its
 * two hard lines while everything around it merges, so the lane looks half-drawn instead of
 * summarised. A single threshold means the answer to "am I looking at tracks or at a stretch
 * of music" is the same everywhere on screen.
 *
 * A span rather than a rate in ms per pixel: the span is what the ruler prints and what the
 * toolbar's presets set, so the "1 h" button keeps its detail on a laptop and on a wall
 * display alike. Below it the per-block rules still apply, and a narrow window still merges
 * most of the lane on width alone.
 */
export const TRACK_MERGE_SPAN_MS = 90 * 60_000;

/** Tracks narrower than this are drawn as tick marks rather than blocks. */
export const TICK_MAX_PX = 6;

/** Extra pixels either side of a tick that still count as a hit, so ticks stay clickable. */
export const HIT_SLOP_PX = 4;

export interface View {
  /** Epoch ms at the centre of the viewport, where the playhead is pinned. */
  centerMs: number;
  msPerPx: number;
  /** Viewport width in CSS pixels. */
  width: number;
}

export function clampZoom(msPerPx: number): number {
  return Math.min(MAX_MS_PER_PX, Math.max(MIN_MS_PER_PX, msPerPx));
}

export function timeToX(view: View, ms: number): number {
  return view.width / 2 + (ms - view.centerMs) / view.msPerPx;
}

export function xToTime(view: View, x: number): number {
  return view.centerMs + (x - view.width / 2) * view.msPerPx;
}

export function viewStart(view: View): number {
  return xToTime(view, 0);
}

export function viewEnd(view: View): number {
  return xToTime(view, view.width);
}

export function viewSpanMs(view: View): number {
  return view.width * view.msPerPx;
}

/**
 * Zoom about a fixed point, so the instant under the cursor stays under the cursor.
 *
 * Zooming about the centre instead would be simpler and would feel wrong: the thing a
 * person is pointing at is the thing they want to keep looking at.
 */
export function zoomAt(view: View, x: number, factor: number): View {
  const anchor = xToTime(view, x);
  const msPerPx = clampZoom(view.msPerPx * factor);
  // Re-derive the centre so that `anchor` still lands on `x` at the new scale.
  const centerMs = anchor + (view.width / 2 - x) * msPerPx;
  return { ...view, msPerPx, centerMs };
}

export function panBy(view: View, dx: number): View {
  return { ...view, centerMs: view.centerMs - dx * view.msPerPx };
}

/**
 * What it takes to read a block that has just been clicked: the span it is drawn across, the
 * instant the pointer was on, and the room its caption needs.
 */
export interface Reveal {
  /** The block's drawn span, in epoch ms. */
  startMs: number;
  endMs: number;
  /** The instant under the pointer, held still while the view closes in around it. */
  anchorMs: number;
  /** How much of the block must be on screen for its caption to be written in full. */
  needPx: number;
  /**
   * The widest view the block's lane still names anything in, however wide the block itself is
   * drawn. The jukebox lane collapses into one unnamed section past `TRACK_MERGE_SPAN_MS`; the
   * show lane has no such ceiling.
   */
  maxViewSpanMs?: number;
}

/**
 * The view that lets a clicked block say what it is, or null when the current one already does.
 *
 * Selecting draws a border, and at a day's zoom that border lands on a sliver two pixels wide:
 * the click has answered "which one" without answering "which one is it". So a click that
 * selects something unreadable also buys it the room to be read.
 *
 * Inward only, and about the instant that was clicked, exactly as the wheel zooms: the thing a
 * person is pointing at is the thing they want to keep looking at, and a click that zoomed out
 * would answer a question nobody asked. Room is not only a matter of zoom, though -- a block
 * wide enough already can still be hanging off the edge of the viewport -- so the last step
 * slides the least distance that puts the room on screen.
 */
export function revealZoom(view: View, reveal: Reveal): View | null {
  const { startMs, endMs, anchorMs, needPx } = reveal;
  if (view.width <= 0) return null;
  const maxViewSpanMs = reveal.maxViewSpanMs ?? Number.POSITIVE_INFINITY;
  // The room the caption has right now: the block's width, clipped to the viewport.
  const roomPx = Math.min(view.width, timeToX(view, endMs)) - Math.max(0, timeToX(view, startMs));
  if (viewSpanMs(view) <= maxViewSpanMs && roomPx >= needPx) return null;

  const spanMs = Math.max(1, endMs - startMs);
  const msPerPx = clampZoom(Math.min(view.msPerPx, spanMs / needPx, maxViewSpanMs / view.width));
  const zoomed = zoomAt(view, timeToX(view, anchorMs), msPerPx / view.msPerPx);

  // What the block can actually be given: its own width at the new scale, which at the end of
  // the zoom range is sometimes less than it asked for.
  const want = Math.min(needPx, spanMs / zoomed.msPerPx);
  const x0 = timeToX(zoomed, startMs);
  const x1 = timeToX(zoomed, endMs);
  const dx = x1 < want ? want - x1 : x0 > zoomed.width - want ? zoomed.width - want - x0 : 0;
  return dx === 0 ? zoomed : panBy(zoomed, dx);
}

/**
 * How far the view must be from the playhead before following it is worth doing.
 *
 * Below a quarter of a pixel nothing geometric changes: the blocks, the gridlines and the
 * fetch window are all where they already were. It also bounds how often the window can be
 * re-derived now that the clock can move the centre on every frame rather than only on a
 * position update.
 */
export const FOLLOW_MIN_DRIFT_PX = 0.25;

/**
 * How far behind the playhead the view may fall in clock terms, whatever the zoom.
 *
 * The pixel threshold alone is not enough now that the ruler's labels carry seconds. At a
 * seven-day zoom a quarter of a pixel is a minute and a half, so the geometry says "nothing
 * has moved" while the clock written across the top of the canvas visibly stops -- which is
 * the complaint this timeline started with, arrived at from the other direction. The canvas
 * already repaints twice a second while the present is on screen, so honouring this costs a
 * fetch-window check rather than a frame.
 */
export const FOLLOW_MAX_LAG_MS = 1000;

/** Whether re-centring on the playhead would change anything a viewer can see. */
export function worthFollowing(view: View, playheadMs: number): boolean {
  const lagMs = Math.abs(playheadMs - view.centerMs);
  return lagMs / view.msPerPx >= FOLLOW_MIN_DRIFT_PX || lagMs >= FOLLOW_MAX_LAG_MS;
}
